import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { ChevronRight, ChevronLeft, CalendarOff, Search, Download, CalendarDays, FileDiff, X, LockKeyhole, BadgeCheck, ClipboardList, Hourglass } from "lucide-react";
import client, { API, getToken } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useT } from "@/lib/i18n";
import { toast } from "sonner";

const norm = (y, m) => {
  const d = new Date(y, m, 1);
  return { y: d.getFullYear(), m: d.getMonth() };
};

const currentCycle = () => {
  const t = new Date();
  let m = t.getMonth();
  if (t.getDate() < 16) m -= 1;
  return norm(t.getFullYear(), m);
};

const iso = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

// Column band colors — a new tint every 7 days (weekly)
const BANDS = [
  { head: "bg-sky-100 text-sky-800", cell: "bg-sky-50/60" },
  { head: "bg-emerald-100 text-emerald-800", cell: "bg-emerald-50/60" },
  { head: "bg-violet-100 text-violet-800", cell: "bg-violet-50/60" },
  { head: "bg-rose-100 text-rose-800", cell: "bg-rose-50/60" },
  { head: "bg-cyan-100 text-cyan-800", cell: "bg-cyan-50/60" },
];

const shiftOn = (assignments, dayIso) => {
  let best = null;
  for (const a of assignments || []) {
    if (a.effective_from <= dayIso && (!best || a.effective_from > best.effective_from)) best = a;
  }
  return best ? best.shift : null;
};

const downloadBlob = async (url, filename) => {
  const res = await fetch(url, { headers: { Authorization: `Bearer ${getToken()}` } });
  if (!res.ok) throw new Error("download failed");
  const blob = await res.blob();
  const href = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = href;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(href);
};

export default function DayOffTracker() {
  const { t, lang } = useT();
  const { user } = useAuth();
  const canAudit = user?.role === "area_manager" || user?.role === "super_admin";
  const [cycle, setCycle] = useState(currentCycle);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerYear, setPickerYear] = useState(cycle.y);
  const [employees, setEmployees] = useState([]);
  const [branches, setBranches] = useState([]);
  const [admins, setAdmins] = useState([]);
  const [dayoffs, setDayoffs] = useState(new Map()); // key "empId|date" -> changed_from|null
  const [confirmed, setConfirmed] = useState(new Set()); // employee ids confirmed for this cycle
  const [shifts, setShifts] = useState({}); // empId -> [assignments]
  const [branchFilter, setBranchFilter] = useState("");
  const [adminFilter, setAdminFilter] = useState("");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [personPick, setPersonPick] = useState(null);
  const [exportMenu, setExportMenu] = useState(false);
  const [shiftEditor, setShiftEditor] = useState(null); // {empId, name, shift, from}
  const [supCodeEditor, setSupCodeEditor] = useState(null); // {id, name, code}
  const [auditOpen, setAuditOpen] = useState(false);
  const [audit, setAudit] = useState(null);
  const [remindSel, setRemindSel] = useState(new Set());
  const [remindNote, setRemindNote] = useState("");
  const [remindSending, setRemindSending] = useState(false);
  const [remindSupFilter, setRemindSupFilter] = useState("");
  const [empNotes, setEmpNotes] = useState({});
  const [lowDaysModal, setLowDaysModal] = useState(null); // {low: [emp], eligible: [emp]}
  const pickerRef = useRef(null);
  const exportRef = useRef(null);
  const gridRef = useRef(null);

  const start = useMemo(() => new Date(cycle.y, cycle.m, 16), [cycle]);
  const end = useMemo(() => new Date(cycle.y, cycle.m + 1, 15), [cycle]);
  const days = useMemo(() => {
    const out = [];
    const cur = new Date(start);
    while (cur <= end) {
      out.push(new Date(cur));
      cur.setDate(cur.getDate() + 1);
    }
    return out;
  }, [start, end]);
  const todayIso = iso(new Date());
  const isCurrent = useMemo(() => {
    const c = currentCycle();
    return c.y === cycle.y && c.m === cycle.m;
  }, [cycle]);

  const locale = lang === "ar" ? "ar-SA-u-ca-gregory" : "en-US";
  const cycleLabel = `${start.toLocaleDateString(locale, { day: "numeric", month: "short" })} — ${end.toLocaleDateString(locale, { day: "numeric", month: "short", year: "numeric" })}`;

  useEffect(() => {
    const close = (e) => {
      if (pickerRef.current && !pickerRef.current.contains(e.target)) setPickerOpen(false);
      if (exportRef.current && !exportRef.current.contains(e.target)) setExportMenu(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  const loadStatic = useCallback(async () => {
    const [empRes, brRes, adRes, shRes] = await Promise.all([
      client.get("/employees"),
      client.get("/branches"),
      client.get("/admins").catch(() => ({ data: [] })),
      client.get("/shifts").catch(() => ({ data: [] })),
    ]);
    setEmployees(empRes.data.filter((e) => e.name));
    setBranches(brRes.data);
    setAdmins(adRes.data || []);
    const byEmp = {};
    for (const a of shRes.data || []) (byEmp[a.employee_id] = byEmp[a.employee_id] || []).push(a);
    setShifts(byEmp);
  }, []);

  const loadDayoffs = useCallback(async () => {
    const [res, confRes, reqRes] = await Promise.all([
      client.get("/dayoffs", { params: { start: iso(start), end: iso(end) } }),
      client.get("/dayoffs/confirmations", { params: { cycle_start: iso(start) } }).catch(() => ({ data: [] })),
      client.get("/dayoffs/unconfirm-requests", { params: { cycle_start: iso(start), status: "pending" } }).catch(() => ({ data: [] })),
    ]);
    setDayoffs(new Map(res.data.map((d) => [`${d.employee_id}|${d.date}`, d.changed_from || null])));
    setConfirmed(new Set(confRes.data || []));
    setPendingReqs(reqRes.data || []);
  }, [start, end]);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        await Promise.all([loadStatic(), loadDayoffs()]);
      } catch {
        toast.error(t("dayoff_load_error"));
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cycle]);

  const branchName = useCallback(
    (id) => branches.find((b) => b.id === id)?.name || "—",
    [branches]
  );

  const scrollToToday = () => {
    const el = gridRef.current?.querySelector(`[data-day="${todayIso}"]`);
    if (el) el.scrollIntoView({ block: "nearest", inline: "center", behavior: "smooth" });
  };

  const filtered = useMemo(() => {
    let sups = branchFilter
      ? []
      : admins.map((a) => ({
          id: a.id,
          name: a.name,
          employee_id: a.employee_code || "",
          branch_id: null,
          admin_id: a.id,
          is_supervisor: true,
        }));
    let list = employees;
    if (branchFilter) list = list.filter((e) => e.branch_id === branchFilter);
    if (adminFilter) {
      list = list.filter((e) => e.admin_id === adminFilter);
      sups = sups.filter((s) => s.id === adminFilter);
    }
    if (q.trim()) {
      const s = q.trim().toLowerCase();
      const match = (e) =>
        (e.name || "").toLowerCase().includes(s) ||
        (e.employee_id || "").toLowerCase().includes(s);
      list = list.filter(match);
      sups = sups.filter(match);
    }
    return [
      ...sups.sort((a, b) => (a.name || "").localeCompare(b.name || "", "ar")),
      ...[...list].sort((a, b) => (a.name || "").localeCompare(b.name || "", "ar")),
    ];
  }, [employees, admins, branchFilter, adminFilter, q]);

  const hasShift = (empId) => shiftOn(shifts[empId] || [], iso(end)) != null;

  const toggle = async (empId, dateStr) => {
    const key = `${empId}|${dateStr}`;
    if (saving) return;
    const person = filtered.find((e) => e.id === empId);
    if (person && !person.is_supervisor && !hasShift(empId)) {
      toast.error(t("dayoff_set_shift_first"));
      setShiftEditor({ empId, name: person.name, shift: null, from: iso(start) });
      return;
    }
    setSaving(key);
    const had = dayoffs.has(key);
    const prevVal = dayoffs.get(key);
    const next = new Map(dayoffs);
    had ? next.delete(key) : next.set(key, null);
    setDayoffs(next);
    try {
      const res = await client.post("/dayoffs/toggle", { employee_id: empId, date: dateStr });
      if (res.data.state === "added" && res.data.changed_from) {
        setDayoffs((m) => {
          const n = new Map(m);
          n.set(key, res.data.changed_from);
          return n;
        });
        toast.info(t("dayoff_change_marked", { from: res.data.changed_from, to: dateStr }));
      }
    } catch (err) {
      setDayoffs((m) => {
        const n = new Map(m);
        had ? n.set(key, prevVal) : n.delete(key);
        return n;
      });
      toast.error(err?.response?.data?.detail || t("dayoff_save_error"));
    } finally {
      setSaving(null);
    }
  };

  const countFor = (empId) => days.reduce((n, d) => n + (dayoffs.has(`${empId}|${iso(d)}`) ? 1 : 0), 0);

  // Full 7-day weeks (color bands) with no day off — partial last band optional
  const missingWeeks = (empId) => {
    const hire = employees.find((e) => e.id === empId)?.hire_date || null;
    const missing = [];
    for (let w = 0; w * 7 + 7 <= days.length; w++) {
      const week = days.slice(w * 7, w * 7 + 7);
      if (hire && hire > iso(week[0])) continue;
      if (!week.some((d) => dayoffs.has(`${empId}|${iso(d)}`))) missing.push(w + 1);
    }
    return missing;
  };

  // Eligible weeks for the employee (weeks starting before hire_date are exempt)
  const requiredMinFor = (empId) => {
    const hire = employees.find((e) => e.id === empId)?.hire_date || null;
    let n = 0;
    for (let w = 0; w * 7 + 7 <= days.length; w++) {
      if (hire && hire > iso(days[w * 7])) continue;
      n++;
    }
    return n;
  };
  const hireMidCycle = (empId) => {
    const hire = employees.find((e) => e.id === empId)?.hire_date || null;
    return hire && hire > iso(start) && hire <= iso(end) ? hire : null;
  };

  const [pendingReqs, setPendingReqs] = useState([]);
  const [unconfirmDlg, setUnconfirmDlg] = useState(null);
  const [unconfirmReason, setUnconfirmReason] = useState("");
  const [unconfirmBusy, setUnconfirmBusy] = useState(false);
  const isSupervisor = user?.role === "supervisor";

  const toggleConfirm = async (empId) => {
    if (confirmed.has(empId)) {
      const person = filtered.find((e) => e.id === empId);
      if (isSupervisor && pendingReqs.some((r) => r.employee_id === empId)) {
        toast.info(t("dayoff_unconfirm_pending_tip"));
        return;
      }
      setUnconfirmReason("");
      setUnconfirmDlg({ empId, name: person?.name || "", mode: isSupervisor ? "request" : "direct" });
      return;
    }
    if (!confirmed.has(empId)) {
      const person = filtered.find((e) => e.id === empId);
      if (person && !person.is_supervisor && !hasShift(empId)) {
        toast.error(t("dayoff_set_shift_first"), { duration: 5000 });
        return;
      }
      const required = requiredMinFor(empId);
      const cnt = countFor(empId);
      if (cnt < required) {
        const hm = hireMidCycle(empId);
        toast.error(
          hm ? t("dayoff_insufficient_days_hire", { n: cnt, total: required, date: hm })
             : t("dayoff_insufficient_days", { n: cnt, total: required }),
          { duration: 6000 }
        );
        return;
      }
    }
    try {
      const res = await client.post("/dayoffs/confirm", { employee_id: empId, cycle_start: iso(start) });
      setConfirmed((s) => {
        const n = new Set(s);
        if (res.data.state === "confirmed") n.add(empId);
        else n.delete(empId);
        return n;
      });
      if (res.data.state === "confirmed" && res.data.warning?.message === "missing_weeks") {
        toast.warning(t("dayoff_confirmed_with_note", { weeks: (res.data.warning.weeks || []).join("، ") }), { duration: 6000 });
      } else {
        toast.success(res.data.state === "confirmed" ? t("dayoff_confirmed_msg") : t("dayoff_unconfirmed_msg"));
      }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail?.message === "missing_shift") {
        toast.error(t("dayoff_set_shift_first"));
      } else if (detail?.message === "insufficient_days") {
        toast.error(detail.hire_mid_cycle
          ? t("dayoff_insufficient_days_hire", { n: detail.count, total: detail.required, date: detail.hire_date })
          : t("dayoff_insufficient_days", { n: detail.count, total: detail.required }));
      } else {
        toast.error(typeof detail === "string" ? detail : t("dayoff_save_error"));
      }
    }
  };

  const submitUnconfirm = async () => {
    const reason = unconfirmReason.trim();
    if (!reason) { toast.error(t("dayoff_unconfirm_reason_required")); return; }
    setUnconfirmBusy(true);
    try {
      if (unconfirmDlg.mode === "request") {
        const res = await client.post("/dayoffs/unconfirm-request", { employee_id: unconfirmDlg.empId, cycle_start: iso(start), reason });
        setPendingReqs((l) => [res.data.request, ...l]);
        toast.success(t("dayoff_unconfirm_request_sent"));
      } else {
        await client.post("/dayoffs/confirm", { employee_id: unconfirmDlg.empId, cycle_start: iso(start), reason });
        setConfirmed((s) => { const n = new Set(s); n.delete(unconfirmDlg.empId); return n; });
        setPendingReqs((l) => l.filter((r) => r.employee_id !== unconfirmDlg.empId));
        toast.success(t("dayoff_unconfirmed_msg"));
      }
      setUnconfirmDlg(null);
    } catch (err) {
      const d = err?.response?.data?.detail;
      if (d?.message === "unconfirm_request_pending") toast.error(t("dayoff_unconfirm_pending_tip"));
      else toast.error(typeof d === "string" ? d : t("dayoff_save_error"));
    } finally { setUnconfirmBusy(false); }
  };

  const decideRequest = async (reqId, approve) => {
    try {
      await client.post(`/dayoffs/unconfirm-requests/${reqId}/decide`, { approve });
      const req = pendingReqs.find((r) => r.id === reqId);
      setPendingReqs((l) => l.filter((r) => r.id !== reqId));
      if (approve) {
        if (req) setConfirmed((s) => { const n = new Set(s); n.delete(req.employee_id); return n; });
        toast.success(t("dayoff_unconfirm_approved_msg"));
      } else {
        toast.success(t("dayoff_unconfirm_rejected_msg"));
      }
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : t("dayoff_save_error"));
    }
  };

  const shiftBadge = (empId) => {
    const list = shifts[empId] || [];
    const atStart = shiftOn(list, iso(start));
    const midChange = list
      .filter((a) => a.effective_from > iso(start) && a.effective_from <= iso(end))
      .sort((a, b) => a.effective_from.localeCompare(b.effective_from));
    return { atStart, midChange };
  };

  const exportExcel = async (which) => {
    setExportMenu(false);
    setExporting(true);
    try {
      const base = new URLSearchParams({ start: iso(start), end: iso(end) });
      if (branchFilter) base.set("branch_id", branchFilter);
      if (adminFilter) base.set("admin_id", adminFilter);
      if (which === 1 || which === "both") {
        await downloadBlob(`${API}/dayoffs/export/csv?${base}&half=1`, "import_roster_template.csv");
      }
      if (which === 2 || which === "both") {
        if (which === "both") await new Promise((r) => setTimeout(r, 600));
        await downloadBlob(`${API}/dayoffs/export/csv?${base}&half=2`, "import_roster_template.csv");
      }
      if (which === "changes1") {
        await downloadBlob(`${API}/dayoffs/export/changes-roster?${base}&half=1`, "import_roster_template_changes.csv");
      }
      if (which === "changes2") {
        await downloadBlob(`${API}/dayoffs/export/changes-roster?${base}&half=2`, "import_roster_template_changes.csv");
      }
      toast.success(t("dayoff_export_done"));
    } catch {
      toast.error(t("dayoff_export_error"));
    } finally {
      setExporting(false);
    }
  };

  const exportPerson = async (p) => {
    setPersonPick(null);
    setExporting(true);
    try {
      const base = new URLSearchParams({ start: iso(start), end: iso(end), person_id: p.id });
      await downloadBlob(`${API}/dayoffs/export/csv?${base}&half=1`, "import_roster_template.csv");
      await new Promise((r) => setTimeout(r, 600));
      await downloadBlob(`${API}/dayoffs/export/csv?${base}&half=2`, "import_roster_template.csv");
      toast.success(t("dayoff_export_done"));
    } catch {
      toast.error(t("dayoff_export_error"));
    } finally {
      setExporting(false);
    }
  };

  const personMatches = useMemo(() => {
    if (!personPick) return [];
    const s = (personPick.q || "").trim().toLowerCase();
    if (!s) return [];
    const sups = admins.map((a) => ({ id: a.id, name: a.name, employee_id: a.employee_code || "", is_supervisor: true }));
    return [...sups, ...employees].filter(
      (e) => (e.name || "").toLowerCase().includes(s) || (e.employee_id || "").toLowerCase().includes(s)
    ).slice(0, 8);
  }, [personPick, admins, employees]);

  const saveShift = async () => {
    if (!shiftEditor.shift) {
      toast.error(t("dayoff_shift_pick_required"));
      return;
    }
    try {
      await client.post("/shifts", {
        employee_id: shiftEditor.empId,
        shift: shiftEditor.shift,
        effective_from: shiftEditor.wholeCycle ? iso(start) : shiftEditor.from,
        apply_whole_cycle: !!shiftEditor.wholeCycle,
      });
      const res = await client.get("/shifts");
      const byEmp = {};
      for (const a of res.data || []) (byEmp[a.employee_id] = byEmp[a.employee_id] || []).push(a);
      setShifts(byEmp);
      setShiftEditor(null);
      toast.success(t("dayoff_shift_saved"));
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("dayoff_save_error"));
    }
  };

  const confirmMany = async (emps) => {
    if (!emps.length) return 0;
    const res = await client.post("/dayoffs/confirm-all", {
      cycle_start: iso(start),
      employee_ids: emps.map((e) => e.id),
    });
    setConfirmed((s) => {
      const n = new Set(s);
      emps.forEach((e) => n.add(e.id));
      return n;
    });
    return res.data.confirmed;
  };

  const confirmAll = async () => {
    const pending = filtered.filter((e) => !confirmed.has(e.id));
    if (!pending.length) {
      toast.info(t("dayoff_all_already_confirmed"));
      return;
    }
    const low = pending.filter((e) => (!e.is_supervisor && !hasShift(e.id)) || countFor(e.id) < requiredMinFor(e.id));
    const eligible = pending.filter((e) => (e.is_supervisor || hasShift(e.id)) && countFor(e.id) >= requiredMinFor(e.id));
    if (low.length) {
      setLowDaysModal({ low, eligible });
      return;
    }
    try {
      await confirmMany(eligible);
      toast.success(t("dayoff_confirm_all_done", { n: eligible.length }));
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("dayoff_save_error"));
    }
  };

  const openAudit = async () => {
    setAuditOpen(true);
    setAudit(null);
    setRemindSel(new Set());
    setRemindNote("");
    setRemindSupFilter("");
    setEmpNotes({});
    try {
      const res = await client.get("/dayoffs/audit", { params: { start: iso(start), end: iso(end) } });
      setAudit(res.data);
    } catch {
      toast.error(t("dayoff_load_error"));
    }
  };

  const saveSupCode = async () => {
    try {
      await client.post("/dayoffs/supervisor-code", {
        admin_id: supCodeEditor.id,
        employee_code: supCodeEditor.code,
      });
      await loadStatic();
      setSupCodeEditor(null);
      toast.success(t("dayoff_sup_code_saved"));
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("dayoff_save_error"));
    }
  };


  const sendReminder = async () => {
    if (!remindSel.size) {
      toast.error(t("dayoff_remind_select_first"));
      return;
    }
    setRemindSending(true);
    try {
      const res = await client.post("/dayoffs/remind", {
        cycle_start: iso(start),
        employee_ids: [...remindSel],
        note: remindNote,
        employee_notes: Object.fromEntries(
          [...remindSel].filter((id) => (empNotes[id] || "").trim()).map((id) => [id, empNotes[id].trim()])
        ),
      });
      toast.success(t("dayoff_remind_sent", { n: res.data.sent_to_supervisors }));
      if (res.data.no_account?.length) {
        toast.warning(t("dayoff_remind_no_account", { names: res.data.no_account.join("، ") }), { duration: 7000 });
      }
      setRemindSel(new Set());
      setRemindNote("");
      setEmpNotes({});
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : t("dayoff_save_error"));
    } finally {
      setRemindSending(false);
    }
  };

  const exportAudit = async () => {
    try {
      await downloadBlob(
        `${API}/dayoffs/audit/xlsx?start=${iso(start)}&end=${iso(end)}`,
        `dayoff_audit_${iso(start)}_${iso(end)}.xlsx`
      );
      toast.success(t("dayoff_export_done"));
    } catch {
      toast.error(t("dayoff_export_error"));
    }
  };

  const monthNames = useMemo(
    () => Array.from({ length: 12 }, (_, i) =>
      new Date(2026, i, 1).toLocaleDateString(locale, { month: "short" })
    ),
    [locale]
  );

  return (
    <div className="space-y-4" data-testid="dayoff-tracker">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="inline-flex items-center justify-center w-11 h-11 rounded-xl bg-gradient-to-br from-amber-500 to-orange-600 text-white shadow">
            <CalendarOff size={22} />
          </div>
          <div>
            <h1 className="text-xl font-black text-slate-800">Day off MenaMe</h1>
            <p className="text-xs text-slate-500">{t("dayoff_subtitle")}</p>
          </div>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          {/* Today's Gregorian date — display only */}
          <div className="flex items-center gap-1.5 bg-white border border-slate-200 rounded-xl px-3 py-2 shadow-sm text-sm font-bold text-slate-700" data-testid="dayoff-today-date">
            <CalendarDays size={14} className="text-teal-600" />
            <span dir="ltr">{new Date().toLocaleDateString("en-GB", { day: "2-digit", month: "long", year: "numeric" })}</span>
          </div>
          {/* Cycle navigation + month picker */}
          <div className="relative" ref={pickerRef}>
            <div className="flex items-center gap-1 bg-white border border-slate-200 rounded-xl px-2 py-1.5 shadow-sm">
              <button
                onClick={() => setCycle((c) => norm(c.y, c.m - 1))}
                className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-600"
                data-testid="dayoff-prev-cycle"
                title={t("dayoff_prev")}
              >
                {lang === "ar" ? <ChevronRight size={18} /> : <ChevronLeft size={18} />}
              </button>
              <button
                onClick={() => { setPickerYear(cycle.y); setPickerOpen((o) => !o); }}
                className="flex items-center gap-1.5 text-sm font-bold text-slate-700 min-w-[165px] justify-center hover:bg-amber-50 rounded-lg px-2 py-1"
                data-testid="dayoff-cycle-label"
                title={t("dayoff_pick_month")}
              >
                <CalendarDays size={14} className="text-amber-600" />
                {cycleLabel}
              </button>
              <button
                onClick={() => setCycle((c) => norm(c.y, c.m + 1))}
                className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-600"
                data-testid="dayoff-next-cycle"
                title={t("dayoff_next")}
              >
                {lang === "ar" ? <ChevronLeft size={18} /> : <ChevronRight size={18} />}
              </button>
              {!isCurrent && (
                <button
                  onClick={() => setCycle(currentCycle())}
                  className="text-xs font-bold text-teal-700 hover:text-teal-900 px-2"
                  data-testid="dayoff-current-cycle"
                >
                  {t("dayoff_current")}
                </button>
              )}
            </div>

            {pickerOpen && (
              <div
                className="absolute top-full mt-2 start-0 z-30 bg-white border border-slate-200 rounded-2xl shadow-xl p-3 w-64"
                data-testid="dayoff-month-picker"
              >
                <div className="flex items-center justify-between mb-2">
                  <button
                    onClick={() => setPickerYear((y) => y - 1)}
                    className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-600"
                    data-testid="dayoff-picker-prev-year"
                  >
                    {lang === "ar" ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}
                  </button>
                  <span className="font-black text-slate-800">{pickerYear}</span>
                  <button
                    onClick={() => setPickerYear((y) => y + 1)}
                    className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-600"
                    data-testid="dayoff-picker-next-year"
                  >
                    {lang === "ar" ? <ChevronLeft size={16} /> : <ChevronRight size={16} />}
                  </button>
                </div>
                <div className="grid grid-cols-3 gap-1.5">
                  {monthNames.map((name, i) => {
                    const active = cycle.y === pickerYear && cycle.m === i;
                    return (
                      <button
                        key={i}
                        onClick={() => { setCycle(norm(pickerYear, i)); setPickerOpen(false); }}
                        className={`py-2 rounded-lg text-xs font-bold transition ${
                          active
                            ? "bg-gradient-to-br from-amber-500 to-orange-600 text-white shadow"
                            : "hover:bg-amber-50 text-slate-700"
                        }`}
                        data-testid={`dayoff-picker-month-${i + 1}`}
                      >
                        {name}
                      </button>
                    );
                  })}
                </div>
                <p className="text-[10px] text-slate-400 mt-2 text-center">{t("dayoff_picker_hint")}</p>
              </div>
            )}
          </div>

          {/* Downloads */}
          <div className="relative" ref={exportRef}>
            <button
              onClick={() => setExportMenu((o) => !o)}
              disabled={exporting}
              className="inline-flex items-center gap-1.5 bg-gradient-to-br from-emerald-600 to-teal-700 text-white text-sm font-bold rounded-xl px-3.5 py-2 shadow hover:opacity-90 disabled:opacity-50"
              data-testid="dayoff-export-excel"
            >
              <Download size={15} />
              {exporting ? t("loading") : t("dayoff_download")}
            </button>
            {exportMenu && (
              <div className="absolute top-full mt-2 end-0 z-30 bg-white border border-slate-200 rounded-2xl shadow-xl p-2 w-64 space-y-1" data-testid="dayoff-export-menu">
                <button
                  onClick={() => exportExcel(1)}
                  className="w-full text-start px-3 py-2.5 rounded-xl text-sm font-bold text-slate-700 hover:bg-emerald-50"
                  data-testid="dayoff-export-file1"
                >
                  📄 {t("dayoff_export_file1")}
                  <div className="text-[10px] text-slate-400 font-normal">
                    16 {start.toLocaleDateString(locale, { month: "long" })} → {t("dayoff_month_end")}
                  </div>
                </button>
                <button
                  onClick={() => exportExcel(2)}
                  className="w-full text-start px-3 py-2.5 rounded-xl text-sm font-bold text-slate-700 hover:bg-emerald-50"
                  data-testid="dayoff-export-file2"
                >
                  📄 {t("dayoff_export_file2")}
                  <div className="text-[10px] text-slate-400 font-normal">
                    1 → 15 {end.toLocaleDateString(locale, { month: "long" })}
                  </div>
                </button>
                <button
                  onClick={() => exportExcel("both")}
                  className="w-full text-start px-3 py-2.5 rounded-xl text-sm font-bold text-emerald-700 hover:bg-emerald-50 border-t border-slate-100"
                  data-testid="dayoff-export-both"
                >
                  ⬇️ {t("dayoff_export_both")}
                </button>
                <button
                  onClick={() => { setExportMenu(false); setPersonPick({ q: "" }); }}
                  className="w-full text-start px-3 py-2.5 rounded-xl text-sm font-bold text-fuchsia-700 hover:bg-fuchsia-50 border-t border-slate-100"
                  data-testid="dayoff-export-person"
                >
                  👤 {t("dayoff_export_person")}
                  <div className="text-[10px] text-slate-400 font-normal">{t("dayoff_export_person_hint")}</div>
                </button>
                <button
                  onClick={() => exportExcel("changes1")}
                  className="w-full text-start px-3 py-2.5 rounded-xl text-sm font-bold text-rose-700 hover:bg-rose-50 border-t border-slate-100"
                  data-testid="dayoff-export-changes-h1"
                >
                  🔄 {t("dayoff_export_changes_only")} — {t("dayoff_export_file1")}
                  <div className="text-[10px] text-slate-400 font-normal">
                    16 {start.toLocaleDateString(locale, { month: "long" })} → {t("dayoff_month_end")}
                  </div>
                </button>
                <button
                  onClick={() => exportExcel("changes2")}
                  className="w-full text-start px-3 py-2.5 rounded-xl text-sm font-bold text-rose-700 hover:bg-rose-50"
                  data-testid="dayoff-export-changes-h2"
                >
                  🔄 {t("dayoff_export_changes_only")} — {t("dayoff_export_file2")}
                  <div className="text-[10px] text-slate-400 font-normal">
                    1 → 15 {end.toLocaleDateString(locale, { month: "long" })}
                  </div>
                </button>
              </div>
            )}
          </div>
          {canAudit && (
            <button
              onClick={openAudit}
              className="inline-flex items-center gap-1.5 bg-white border border-indigo-300 text-indigo-700 text-sm font-bold rounded-xl px-3.5 py-2 shadow-sm hover:bg-indigo-50"
              data-testid="dayoff-audit-button"
            >
              <ClipboardList size={15} />
              {t("dayoff_audit")}
            </button>
          )}
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative">
          <Search size={14} className="absolute top-1/2 -translate-y-1/2 start-2.5 text-slate-400" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={t("dayoff_search")}
            className="ps-8 pe-3 py-2 rounded-xl border border-slate-200 bg-white text-sm w-52 focus:outline-none focus:ring-2 focus:ring-amber-400"
            data-testid="dayoff-search-input"
          />
        </div>
        <select
          value={branchFilter}
          onChange={(e) => setBranchFilter(e.target.value)}
          className="px-3 py-2 rounded-xl border border-slate-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
          data-testid="dayoff-branch-filter"
        >
          <option value="">{t("dayoff_all_branches")}</option>
          {branches.map((b) => (
            <option key={b.id} value={b.id}>{b.name}</option>
          ))}
        </select>
        {admins.length > 0 && (
          <select
            value={adminFilter}
            onChange={(e) => setAdminFilter(e.target.value)}
            className="px-3 py-2 rounded-xl border border-slate-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
            data-testid="dayoff-admin-filter"
          >
            <option value="">{t("dayoff_all_supervisors")}</option>
            {admins.map((a) => (
              <option key={a.id} value={a.id}>{a.name}</option>
            ))}
          </select>
        )}
        <span className="text-xs text-slate-500" data-testid="dayoff-emp-count">
          {filtered.length} {t("dayoff_employees_word")}
        </span>
        {isCurrent && (
          <button
            onClick={scrollToToday}
            className="inline-flex items-center gap-1.5 bg-sky-50 border border-sky-300 text-sky-700 text-xs font-bold rounded-xl px-3 py-2 hover:bg-sky-100"
            data-testid="dayoff-goto-today"
          >
            <CalendarDays size={14} />
            {t("dayoff_goto_today")}
          </button>
        )}
        <button
          onClick={confirmAll}
          className="inline-flex items-center gap-1.5 bg-emerald-50 border border-emerald-300 text-emerald-700 text-xs font-bold rounded-xl px-3 py-2 hover:bg-emerald-100"
          data-testid="dayoff-confirm-all"
        >
          <BadgeCheck size={14} />
          {t("dayoff_confirm_all")}
        </button>
      </div>

      <p className="text-xs text-slate-400">{t("dayoff_hint")}</p>

      {canAudit && pendingReqs.length > 0 && (
        <div className="rounded-2xl border border-amber-300 bg-amber-50 p-4 space-y-3" data-testid="dayoff-unconfirm-requests-panel">
          <div className="flex items-center gap-2 font-black text-amber-900 text-sm">
            <Hourglass size={15} /> {t("dayoff_unconfirm_requests_title")} ({pendingReqs.length})
          </div>
          {pendingReqs.map((r) => (
            <div key={r.id} className="bg-white rounded-xl border border-amber-200 p-3 flex flex-wrap items-center gap-3" data-testid={`unconfirm-req-${r.id}`}>
              <div className="min-w-0 flex-1">
                <div className="font-bold text-slate-800 text-sm">{r.employee_name}</div>
                <div className="text-xs text-slate-500">{t("dayoff_unconfirm_req_by")}: {r.requested_by_name} · {new Date(r.requested_at).toLocaleString(locale)}</div>
                <div className="text-xs text-slate-700 mt-1">📝 {r.reason}</div>
              </div>
              <div className="flex gap-2">
                <button onClick={() => decideRequest(r.id, true)} className="px-3 py-1.5 rounded-lg bg-emerald-600 text-white text-xs font-bold hover:bg-emerald-700" data-testid={`unconfirm-approve-${r.id}`}>{t("dayoff_approve_unconfirm")}</button>
                <button onClick={() => decideRequest(r.id, false)} className="px-3 py-1.5 rounded-lg bg-rose-100 text-rose-700 text-xs font-bold hover:bg-rose-200" data-testid={`unconfirm-reject-${r.id}`}>{t("dayoff_reject_unconfirm")}</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {unconfirmDlg && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="unconfirm-dialog">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-5 space-y-3">
            <div className="font-black text-slate-800">{t("dayoff_unconfirm_title", { name: unconfirmDlg.name })}</div>
            <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-2.5">
              {unconfirmDlg.mode === "request" ? t("dayoff_unconfirm_request_warn") : t("dayoff_unconfirm_warn")}
            </p>
            <div>
              <label className="text-xs font-bold text-slate-500">{t("dayoff_unconfirm_reason_label")} *</label>
              <textarea
                value={unconfirmReason}
                onChange={(e) => setUnconfirmReason(e.target.value)}
                rows={3}
                className="w-full mt-1 rounded-xl border border-slate-200 p-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
                placeholder={t("dayoff_unconfirm_reason_ph")}
                data-testid="unconfirm-reason-input"
              />
            </div>
            <div className="flex gap-2 justify-end">
              <button onClick={() => setUnconfirmDlg(null)} className="px-4 py-2 rounded-xl bg-slate-100 text-slate-600 text-sm font-bold hover:bg-slate-200" data-testid="unconfirm-cancel-btn">{t("cancel")}</button>
              <button
                onClick={submitUnconfirm}
                disabled={unconfirmBusy || !unconfirmReason.trim()}
                className="px-4 py-2 rounded-xl bg-rose-600 text-white text-sm font-bold disabled:opacity-50 hover:bg-rose-700"
                data-testid="unconfirm-submit-btn"
              >
                {unconfirmBusy ? t("saving") : unconfirmDlg.mode === "request" ? t("dayoff_unconfirm_request_btn") : t("dayoff_unconfirm_direct_btn")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Grid */}
      {loading ? (
        <div className="text-center py-16 text-slate-400 text-sm">{t("loading")}</div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16 text-slate-400 text-sm" data-testid="dayoff-empty">
          {t("dayoff_no_employees")}
        </div>
      ) : (
        <div
          ref={gridRef}
          className="overflow-x-auto rounded-2xl border border-slate-200 bg-white shadow-sm"
          style={{ WebkitOverflowScrolling: "touch", scrollBehavior: "smooth" }}
          data-testid="dayoff-grid"
        >
          <table className="text-xs border-collapse min-w-max">
            <thead>
              <tr className="bg-slate-50">
                <th className="sticky start-0 z-10 bg-slate-50 border-b border-e border-slate-200 px-2 sm:px-3 py-2 text-start min-w-[130px] sm:min-w-[180px]">
                  {t("dayoff_employee")}
                </th>
                <th className="border-b border-e border-slate-200 px-2 py-2 text-center bg-slate-50 min-w-[70px] text-slate-500">
                  {t("dayoff_emp_no")}
                </th>
                <th className="border-b border-e border-slate-200 px-2 py-2 text-center bg-slate-50 min-w-[70px] text-slate-500">
                  {t("dayoff_shift")}
                </th>
                {days.map((d, idx) => {
                  const isToday = iso(d) === todayIso;
                  const band = BANDS[Math.floor(idx / 7) % BANDS.length];
                  return (
                    <th
                      key={iso(d)}
                      data-day={iso(d)}
                      className={`border-b border-slate-200 px-1 py-1.5 text-center min-w-[42px] font-semibold ${
                        isToday ? "bg-amber-200 text-amber-900" : band.head
                      }`}
                    >
                      <div className="text-[10px]">{d.toLocaleDateString(locale, { weekday: "short" })}</div>
                      <div className="text-[11px] font-black">{d.getDate()}/{d.getMonth() + 1}</div>
                    </th>
                  );
                })}
                <th className="border-b border-s border-slate-200 px-2 py-2 text-center bg-slate-50 min-w-[50px]">
                  {t("dayoff_total")}
                </th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((emp, i) => {
                const { atStart, midChange } = shiftBadge(emp.id);
                const isConfirmed = confirmed.has(emp.id);
                const hasPendingReq = pendingReqs.some((r) => r.employee_id === emp.id);
                return (
                  <tr key={emp.id} className={i % 2 ? "bg-slate-50/50" : "bg-white"}>
                    <td className="sticky start-0 z-10 border-e border-b border-slate-100 px-2 sm:px-3 py-1.5" style={{ backgroundColor: i % 2 ? "#f8fafc" : "#fff" }}>
                      <div className="flex items-center gap-1.5">
                        <button
                          onClick={() => toggleConfirm(emp.id)}
                          className={`shrink-0 inline-flex items-center justify-center w-6 h-6 rounded-full transition ${
                            isConfirmed && hasPendingReq
                              ? "bg-amber-400 text-white shadow hover:bg-amber-500"
                              : isConfirmed
                              ? "bg-emerald-500 text-white shadow hover:bg-emerald-600"
                              : "bg-slate-100 text-slate-400 hover:bg-emerald-100 hover:text-emerald-600"
                          }`}
                          data-testid={`dayoff-confirm-${emp.id}`}
                          title={isConfirmed && hasPendingReq ? t("dayoff_unconfirm_pending_tip") : isConfirmed ? t("dayoff_confirmed_tip") : t("dayoff_confirm_tip")}
                        >
                          {isConfirmed ? (hasPendingReq ? <Hourglass size={13} /> : <BadgeCheck size={14} />) : <LockKeyhole size={12} />}
                        </button>
                        <div className="min-w-0">
                          <div className="font-bold text-slate-800 break-words leading-tight max-w-[110px] sm:max-w-[170px] text-[11px] sm:text-xs">{emp.name}</div>
                          {emp.is_supervisor ? (
                            <div className="text-[9px] sm:text-[10px] font-black text-violet-600">{t("dayoff_supervisor_label")}</div>
                          ) : (
                            <div className="text-[9px] sm:text-[10px] text-slate-400 truncate max-w-[110px] sm:max-w-[170px]">{branchName(emp.branch_id)}</div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className={`border-e border-b border-slate-100 text-center font-bold ${!emp.is_supervisor && countFor(emp.id) < requiredMinFor(emp.id) ? "bg-rose-100 text-rose-700" : "text-slate-600"}`} data-testid={`dayoff-empno-${emp.id}`}>
                      {emp.employee_id || "—"}
                    </td>
                    <td className="border-e border-b border-slate-100 text-center p-1">
                      {emp.is_supervisor ? (
                        <button
                          onClick={() => setSupCodeEditor({ id: emp.id, name: emp.name, code: emp.employee_id || "" })}
                          className="inline-flex items-center rounded-lg px-2 py-1 text-[10px] font-black bg-violet-100 text-violet-800 transition hover:ring-2 hover:ring-violet-300"
                          data-testid={`dayoff-shift-${emp.id}`}
                          title={t("dayoff_sup_code_title")}
                        >
                          27
                        </button>
                      ) : (
                      <button
                        onClick={() => setShiftEditor({ empId: emp.id, name: emp.name, shift: atStart, from: iso(start) })}
                        className={`inline-flex flex-col items-center rounded-lg px-2 py-1 text-[10px] font-black transition hover:ring-2 hover:ring-amber-300 ${
                          atStart === "AM"
                            ? "bg-sky-100 text-sky-800"
                            : atStart === "PM"
                            ? "bg-indigo-100 text-indigo-800"
                            : "bg-amber-50 text-amber-700 border border-dashed border-amber-400"
                        }`}
                        data-testid={`dayoff-shift-${emp.id}`}
                        title={t("dayoff_shift_edit")}
                      >
                        <span className="whitespace-nowrap">{atStart || t("dayoff_shift_unset")}</span>
                        {midChange.length > 0 && (
                          <span className="text-[9px] text-rose-600 font-bold whitespace-nowrap">
                            →{midChange[midChange.length - 1].shift}{" "}
                            {new Date(midChange[midChange.length - 1].effective_from).getDate()}/
                            {new Date(midChange[midChange.length - 1].effective_from).getMonth() + 1}
                          </span>
                        )}
                      </button>
                      )}
                    </td>
                    {days.map((d, idx) => {
                      const dateStr = iso(d);
                      const key = `${emp.id}|${dateStr}`;
                      const on = dayoffs.has(key);
                      const changedFrom = dayoffs.get(key);
                      const isToday = dateStr === todayIso;
                      const band = BANDS[Math.floor(idx / 7) % BANDS.length];
                      return (
                        <td key={dateStr} className={`border-b border-slate-100 p-0.5 text-center ${isToday ? "bg-amber-100/70" : band.cell}`}>
                          <button
                            onClick={() => toggle(emp.id, dateStr)}
                            disabled={saving === key}
                            className={`relative w-8 h-8 rounded-lg text-[13px] font-black transition-colors ${
                              on
                                ? changedFrom
                                  ? "bg-gradient-to-br from-rose-500 to-orange-600 text-white shadow ring-2 ring-rose-300"
                                  : "bg-gradient-to-br from-amber-500 to-orange-600 text-white shadow"
                                : "bg-transparent hover:bg-amber-200/60 text-transparent hover:text-amber-500"
                            }`}
                            data-testid={`dayoff-cell-${emp.id}-${dateStr}`}
                            title={
                              on && changedFrom
                                ? t("dayoff_changed_from", { from: changedFrom })
                                : `${emp.name} — ${dateStr}`
                            }
                          >
                            ✓
                            {on && changedFrom && (
                              <span className="absolute -top-1 -end-1 w-3 h-3 bg-rose-600 border-2 border-white rounded-full" />
                            )}
                          </button>
                        </td>
                      );
                    })}
                    <td className={`border-b border-s border-slate-100 text-center font-black ${countFor(emp.id) >= requiredMinFor(emp.id) ? "text-emerald-600 bg-emerald-50/60" : "text-rose-600 bg-rose-50/60"}`} data-testid={`dayoff-total-${emp.id}`}>
                      {countFor(emp.id)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Audit drawer (AM / SA) */}
      {auditOpen && (
        <div className="fixed inset-0 z-40 bg-black/40 flex justify-end" data-testid="dayoff-audit-drawer" onClick={() => setAuditOpen(false)}>
          <div className="bg-white h-full w-full max-w-md shadow-2xl flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-4 border-b border-slate-200 bg-gradient-to-l from-indigo-600 to-violet-700 text-white">
              <div>
                <h3 className="font-black text-sm flex items-center gap-2"><ClipboardList size={16} /> {t("dayoff_audit")}</h3>
                <p className="text-[11px] opacity-80 mt-0.5">{cycleLabel}</p>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={exportAudit}
                  className="inline-flex items-center gap-1 bg-white/20 hover:bg-white/30 rounded-lg px-2.5 py-1.5 text-[11px] font-bold"
                  data-testid="dayoff-audit-download"
                >
                  <Download size={12} /> {t("dayoff_audit_download")}
                </button>
                <button onClick={() => setAuditOpen(false)} className="p-1.5 rounded-lg hover:bg-white/20" data-testid="dayoff-audit-close">
                  <X size={16} />
                </button>
              </div>
            </div>
            <div className="flex-1 overflow-y-auto p-4 space-y-5">
              {!audit ? (
                <div className="text-center py-10 text-slate-400 text-sm">{t("loading")}</div>
              ) : (
                <>
                  <div className="grid grid-cols-2 gap-2 text-center">
                    <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3">
                      <div className="text-2xl font-black text-emerald-700" data-testid="audit-confirmed-count">{audit.confirmed_count}</div>
                      <div className="text-[11px] text-emerald-600 font-bold">{t("dayoff_audit_confirmed")}</div>
                    </div>
                    <div className="bg-rose-50 border border-rose-200 rounded-xl p-3">
                      <div className="text-2xl font-black text-rose-700" data-testid="audit-unconfirmed-count">{audit.unconfirmed.length}</div>
                      <div className="text-[11px] text-rose-600 font-bold">{t("dayoff_audit_unconfirmed")}</div>
                    </div>
                  </div>

                  {/* Unconfirmed employees */}
                  <section>
                    <h4 className="text-xs font-black text-rose-700 mb-2 flex items-center gap-1.5">
                      <LockKeyhole size={13} /> {t("dayoff_audit_unconfirmed_title")} ({audit.unconfirmed.length})
                    </h4>
                    {audit.unconfirmed.length === 0 ? (
                      <p className="text-xs text-emerald-600 font-bold bg-emerald-50 rounded-xl p-3">✓ {t("dayoff_audit_all_confirmed")}</p>
                    ) : (
                      <>
                        <select
                          value={remindSupFilter}
                          onChange={(e) => { setRemindSupFilter(e.target.value); setRemindSel(new Set()); }}
                          className="w-full mb-2 px-3 py-2 rounded-xl border border-slate-200 bg-white text-xs font-bold focus:outline-none focus:ring-2 focus:ring-rose-300"
                          data-testid="audit-remind-sup-filter"
                        >
                          <option value="">{t("dayoff_all_supervisors")}</option>
                          {[...new Set(audit.unconfirmed.map((x) => x.supervisor).filter(Boolean))].map((s) => (
                            <option key={s} value={s}>{s}</option>
                          ))}
                        </select>
                        {(() => {
                          const shown = remindSupFilter
                            ? audit.unconfirmed.filter((x) => x.supervisor === remindSupFilter)
                            : audit.unconfirmed;
                          return (
                            <>
                              <div className="flex items-center justify-between mb-2">
                                <label className="flex items-center gap-1.5 text-[11px] font-bold text-slate-500 cursor-pointer">
                                  <input
                                    type="checkbox"
                                    checked={shown.length > 0 && shown.every((x) => remindSel.has(x.id))}
                                    onChange={(e) =>
                                      setRemindSel(e.target.checked ? new Set(shown.map((x) => x.id)) : new Set())
                                    }
                                    className="accent-rose-600"
                                    data-testid="audit-remind-select-all"
                                  />
                                  {t("dayoff_remind_select_all")}
                                </label>
                                <span className="text-[10px] text-slate-400">{remindSel.size} {t("dayoff_remind_selected")}</span>
                              </div>
                              <div className="space-y-1.5" data-testid="audit-unconfirmed-list">
                                {shown.map((e) => (
                                  <div key={e.id} className="bg-rose-50/50 border border-rose-100 rounded-xl px-3 py-2 hover:border-rose-300">
                                    <label className="flex items-center gap-2 cursor-pointer">
                                      <input
                                        type="checkbox"
                                        checked={remindSel.has(e.id)}
                                        onChange={(ev) =>
                                          setRemindSel((s) => {
                                            const n = new Set(s);
                                            ev.target.checked ? n.add(e.id) : n.delete(e.id);
                                            return n;
                                          })
                                        }
                                        className="accent-rose-600 shrink-0"
                                        data-testid={`audit-remind-check-${e.id}`}
                                      />
                                      <div className="min-w-0">
                                        <div className="text-xs font-bold text-slate-800 truncate">{e.name} <span className="text-slate-400 text-[10px]">[{e.employee_id || "—"}]</span></div>
                                        <div className="text-[10px] text-slate-400 truncate">{e.branch || "—"} · {e.supervisor || "—"}</div>
                                      </div>
                                    </label>
                                    {remindSel.has(e.id) && (
                                      <input
                                        value={empNotes[e.id] || ""}
                                        onChange={(ev) => setEmpNotes((m) => ({ ...m, [e.id]: ev.target.value }))}
                                        placeholder={t("dayoff_remind_emp_note_ph")}
                                        className="w-full mt-2 px-2.5 py-1.5 rounded-lg border border-amber-300 bg-amber-50/60 text-[11px] focus:outline-none focus:ring-2 focus:ring-amber-300"
                                        data-testid={`audit-remind-note-${e.id}`}
                                      />
                                    )}
                                  </div>
                                ))}
                              </div>
                            </>
                          );
                        })()}
                        <div className="mt-3 space-y-2 bg-slate-50 border border-slate-200 rounded-xl p-3">
                          <label className="text-[11px] font-bold text-slate-500 block">{t("dayoff_remind_note_label")}</label>
                          <textarea
                            value={remindNote}
                            onChange={(e) => setRemindNote(e.target.value)}
                            placeholder={t("dayoff_remind_note_ph")}
                            rows={2}
                            className="w-full px-3 py-2 rounded-xl border border-slate-200 text-xs focus:outline-none focus:ring-2 focus:ring-rose-300 resize-none"
                            data-testid="audit-remind-note"
                          />
                          <button
                            onClick={sendReminder}
                            disabled={remindSending || !remindSel.size}
                            className="w-full bg-gradient-to-br from-rose-600 to-orange-600 text-white font-bold text-xs rounded-xl py-2.5 shadow hover:opacity-90 disabled:opacity-40"
                            data-testid="audit-remind-send"
                          >
                            {remindSending ? t("loading") : t("dayoff_remind_send", { n: remindSel.size })}
                          </button>
                        </div>
                      </>
                    )}
                  </section>

                  {/* Changes */}
                  <section>
                    <h4 className="text-xs font-black text-indigo-700 mb-2 flex items-center gap-1.5">
                      <FileDiff size={13} /> {t("dayoff_audit_changes_title")} ({audit.moved.length + audit.cancelled.length})
                    </h4>
                    {audit.moved.length + audit.cancelled.length === 0 ? (
                      <p className="text-xs text-slate-400 bg-slate-50 rounded-xl p-3">{t("dayoff_audit_no_changes")}</p>
                    ) : (
                      <div className="space-y-1.5" data-testid="audit-changes-list">
                        {audit.moved.map((m) => (
                          <div key={m.id} className="bg-amber-50/60 border border-amber-200 rounded-xl px-3 py-2">
                            <div className="text-xs font-bold text-slate-800">{m.employee_name} <span className="text-slate-400 text-[10px]">[{m.employee_code || "—"}]</span></div>
                            <div className="text-[11px] text-slate-600 mt-0.5">
                              <span className="line-through text-rose-600">{m.changed_from}</span> ← <b className="text-emerald-700">{m.date}</b>
                            </div>
                            <div className="text-[10px] text-slate-400 mt-0.5">{m.branch || "—"} · {t("dayoff_audit_by")} {m.actor_name} · <span style={{ direction: "ltr", display: "inline-block" }}>{(m.at || "").slice(0, 16).replace("T", " ")}</span></div>
                          </div>
                        ))}
                        {audit.cancelled.map((c) => (
                          <div key={c.id} className="bg-rose-50/60 border border-rose-200 rounded-xl px-3 py-2">
                            <div className="text-xs font-bold text-slate-800">{c.employee_name} <span className="text-slate-400 text-[10px]">[{c.employee_code || "—"}]</span></div>
                            <div className="text-[11px] text-rose-700 mt-0.5">{t("dayoff_audit_cancelled")} {c.date}</div>
                            <div className="text-[10px] text-slate-400 mt-0.5">{c.branch || "—"} · {t("dayoff_audit_by")} {c.actor_name} · <span style={{ direction: "ltr", display: "inline-block" }}>{(c.at || "").slice(0, 16).replace("T", " ")}</span></div>
                          </div>
                        ))}
                      </div>
                    )}
                  </section>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Low day-off warning modal */}
      {lowDaysModal && (
        <div className="fixed inset-0 z-40 bg-black/40 flex items-center justify-center p-4" data-testid="dayoff-lowdays-modal">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-5 space-y-4 max-h-[85vh] overflow-y-auto">
            <div className="flex items-center justify-between">
              <h3 className="font-black text-amber-700 text-sm">⚠️ {t("dayoff_lowdays_title")}</h3>
              <button onClick={() => setLowDaysModal(null)} className="p-1 rounded-lg hover:bg-slate-100" data-testid="dayoff-lowdays-close">
                <X size={16} />
              </button>
            </div>
            <p className="text-xs text-slate-500">{t("dayoff_lowdays_hint", { n: lowDaysModal.low.length })}</p>
            <div className="space-y-1.5">
              {lowDaysModal.low.map((e) => (
                <div key={e.id} className="flex items-center justify-between bg-amber-50 border border-amber-200 rounded-xl px-3 py-2">
                  <div className="text-xs font-bold text-slate-800 truncate">
                    {e.name} <span className="text-slate-400 text-[10px]">[{e.employee_id || "—"}]</span>
                  </div>
                  <span className="text-[11px] font-black text-amber-700 whitespace-nowrap">
                    {!e.is_supervisor && !hasShift(e.id)
                      ? t("dayoff_shift_unset")
                      : t("dayoff_insufficient_days", { n: countFor(e.id), total: requiredMinFor(e.id) }).replace("⚠️ ", "")}
                  </span>
                </div>
              ))}
            </div>
            <div className="flex flex-col gap-2">
              {lowDaysModal.eligible.length > 0 && (
                <button
                  onClick={async () => {
                    try {
                      await confirmMany(lowDaysModal.eligible);
                      toast.success(t("dayoff_confirm_all_done", { n: lowDaysModal.eligible.length }));
                    } catch { toast.error(t("dayoff_save_error")); }
                    setLowDaysModal(null);
                  }}
                  className="w-full bg-gradient-to-br from-emerald-600 to-teal-700 text-white font-bold text-sm rounded-xl py-2.5 shadow hover:opacity-90"
                  data-testid="dayoff-lowdays-confirm-eligible"
                >
                  {t("dayoff_confirm_eligible_only", { n: lowDaysModal.eligible.length })}
                </button>
              )}
              <button
                onClick={() => setLowDaysModal(null)}
                className="w-full bg-slate-100 text-slate-600 font-bold text-sm rounded-xl py-2.5 hover:bg-slate-200"
                data-testid="dayoff-lowdays-cancel"
              >
                {t("dayoff_cancel")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Supervisor code modal */}
      {supCodeEditor && (
        <div className="fixed inset-0 z-40 bg-black/40 flex items-center justify-center p-4" data-testid="dayoff-supcode-modal">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-5 space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="font-black text-slate-800 text-sm">{t("dayoff_sup_code_title")}</h3>
              <button onClick={() => setSupCodeEditor(null)} className="p-1 rounded-lg hover:bg-slate-100">
                <X size={16} />
              </button>
            </div>
            <p className="text-xs text-slate-500 font-bold">{supCodeEditor.name} <span className="text-violet-600">({t("dayoff_supervisor_label")} — 27)</span></p>
            <input
              value={supCodeEditor.code}
              onChange={(e) => setSupCodeEditor((s) => ({ ...s, code: e.target.value }))}
              placeholder={t("dayoff_emp_no")}
              className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-violet-400"
              data-testid="dayoff-supcode-input"
            />
            <div className="flex gap-2">
              <button
                onClick={saveSupCode}
                className="flex-1 bg-gradient-to-br from-violet-600 to-purple-700 text-white font-bold text-sm rounded-xl py-2.5 shadow hover:opacity-90"
                data-testid="dayoff-supcode-save"
              >
                {t("dayoff_shift_save")}
              </button>
              <button onClick={() => setSupCodeEditor(null)} className="px-4 bg-slate-100 text-slate-600 font-bold text-sm rounded-xl py-2.5 hover:bg-slate-200">
                {t("dayoff_cancel")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Person export picker modal */}
      {personPick && (
        <div className="fixed inset-0 z-40 bg-black/40 flex items-center justify-center p-4" data-testid="dayoff-person-modal">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-5 space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="font-black text-slate-800 text-sm flex items-center gap-1.5">
                👤 {t("dayoff_person_modal_title")}
              </h3>
              <button onClick={() => setPersonPick(null)} className="p-1 rounded-lg hover:bg-slate-100" data-testid="dayoff-person-close">
                <X size={16} />
              </button>
            </div>
            <div className="relative">
              <Search size={14} className="absolute top-1/2 -translate-y-1/2 start-2.5 text-slate-400" />
              <input
                autoFocus
                value={personPick.q}
                onChange={(e) => setPersonPick((s) => ({ ...s, q: e.target.value }))}
                placeholder={t("dayoff_person_search_ph")}
                className="w-full ps-8 pe-3 py-2 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-fuchsia-400"
                data-testid="dayoff-person-search"
              />
            </div>
            <div className="max-h-64 overflow-y-auto space-y-1" data-testid="dayoff-person-results">
              {!(personPick.q || "").trim() && (
                <div className="text-xs text-slate-400 text-center py-4">{t("dayoff_person_type_hint")}</div>
              )}
              {(personPick.q || "").trim() && personMatches.length === 0 && (
                <div className="text-xs text-slate-400 text-center py-4" data-testid="dayoff-person-no-match">{t("dayoff_person_no_match")}</div>
              )}
              {personMatches.map((p) => {
                const noCode = !(p.employee_id || "").trim();
                return (
                  <button
                    key={p.id}
                    onClick={() => !noCode && exportPerson(p)}
                    disabled={noCode}
                    className={`w-full text-start px-3 py-2 rounded-xl border text-sm flex items-center justify-between gap-2 ${noCode ? "border-slate-100 text-slate-300 cursor-not-allowed" : "border-slate-200 hover:border-fuchsia-400 hover:bg-fuchsia-50 text-slate-700"}`}
                    data-testid={`dayoff-person-row-${p.id}`}
                  >
                    <span className="font-bold truncate">
                      {p.name}
                      {p.is_supervisor && <span className="text-[9px] font-bold bg-violet-100 text-violet-700 px-1.5 py-0.5 rounded-full ms-1.5">{t("dayoff_supervisor_label")}</span>}
                    </span>
                    <span className="text-[11px] text-slate-400 shrink-0" style={{ direction: "ltr" }}>
                      {noCode ? t("dayoff_person_no_code") : `[${p.employee_id}]`}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Shift editor modal */}
      {shiftEditor && (
        <div className="fixed inset-0 z-40 bg-black/40 flex items-center justify-center p-4" data-testid="dayoff-shift-modal">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-5 space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="font-black text-slate-800 text-sm">{t("dayoff_shift_title")}</h3>
              <button onClick={() => setShiftEditor(null)} className="p-1 rounded-lg hover:bg-slate-100" data-testid="dayoff-shift-close">
                <X size={16} />
              </button>
            </div>
            <p className="text-xs text-slate-500 font-bold">{shiftEditor.name}</p>
            <div className="grid grid-cols-2 gap-2">
              {["AM", "PM"].map((s) => (
                <button
                  key={s}
                  onClick={() => setShiftEditor((e) => ({ ...e, shift: s }))}
                  className={`py-2.5 rounded-xl font-black text-sm transition ${
                    shiftEditor.shift === s
                      ? s === "AM"
                        ? "bg-sky-600 text-white shadow"
                        : "bg-indigo-600 text-white shadow"
                      : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                  }`}
                  data-testid={`dayoff-shift-pick-${s}`}
                >
                  {s} <span className="text-[10px] opacity-70">({s === "AM" ? "25" : "26"})</span>
                </button>
              ))}
            </div>
            <div>
              <label className="flex items-center gap-2 mb-2 cursor-pointer bg-violet-50 border border-violet-200 rounded-xl px-3 py-2">
                <input
                  type="checkbox"
                  checked={!!shiftEditor.wholeCycle}
                  onChange={(e) => setShiftEditor((s) => ({ ...s, wholeCycle: e.target.checked }))}
                  className="accent-violet-600"
                  data-testid="dayoff-shift-whole-cycle"
                />
                <span className="text-[11px] font-bold text-violet-800">{t("dayoff_shift_whole_cycle")}</span>
              </label>
              <label className="text-[11px] font-bold text-slate-500 block mb-1">{t("dayoff_shift_from")}</label>
              <input
                type="date"
                value={shiftEditor.wholeCycle ? iso(start) : shiftEditor.from}
                min={iso(start)}
                max={iso(end)}
                disabled={!!shiftEditor.wholeCycle}
                onChange={(e) => setShiftEditor((s) => ({ ...s, from: e.target.value }))}
                className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400 disabled:bg-slate-100 disabled:text-slate-400"
                data-testid="dayoff-shift-from-input"
              />
              <p className="text-[10px] text-slate-400 mt-1">{t("dayoff_shift_from_hint")}</p>
            </div>
            <div className="flex gap-2">
              <button
                onClick={saveShift}
                className="flex-1 bg-gradient-to-br from-amber-500 to-orange-600 text-white font-bold text-sm rounded-xl py-2.5 shadow hover:opacity-90"
                data-testid="dayoff-shift-save"
              >
                {t("dayoff_shift_save")}
              </button>
              <button
                onClick={() => setShiftEditor(null)}
                className="px-4 bg-slate-100 text-slate-600 font-bold text-sm rounded-xl py-2.5 hover:bg-slate-200"
                data-testid="dayoff-shift-cancel"
              >
                {t("dayoff_cancel")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
